/**
 * S3 connections in SDL text: reading an S3 instance's keys out of the SDL it
 * was signed with, and the SDL editor's "Insert S3 connection".
 *
 * Kept apart from `s3Connection.ts` because it needs the YAML parser, which
 * the guided Configure page does not otherwise load.
 */

import { isMap, isScalar, isSeq, parseDocument, type Document, type YAMLSeq } from 'yaml'

import type { GuidedEnvironment } from './guided'
import { isS3InstanceImage, s3CredentialsFromEnv, type S3InstanceCredentials } from './s3Connection'

const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

function envRows(value: unknown): GuidedEnvironment[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item): GuidedEnvironment[] => {
    if (typeof item !== 'string') return []
    const separator = item.indexOf('=')
    if (separator <= 0) return []
    return [{ key: item.slice(0, separator).trim(), value: item.slice(separator + 1) }]
  })
}

function parsed(text: string): Document.Parsed | null {
  try {
    const doc = parseDocument(text)
    return doc.errors.length > 0 ? null : doc
  } catch {
    return null
  }
}

/**
 * The service that runs an S3 instance image in a signed SDL, with the keys
 * it was deployed with; null when no service runs one or the text does not
 * parse.
 */
export function s3InstanceFromSdl(text: string): { service: string; credentials: S3InstanceCredentials } | null {
  const doc = parsed(text)
  const services = doc?.toJS() as { services?: Record<string, { image?: unknown; env?: unknown }> } | null | undefined
  for (const [service, spec] of Object.entries(services?.services ?? {})) {
    if (typeof spec?.image === 'string' && isS3InstanceImage(spec.image)) return { service, credentials: s3CredentialsFromEnv(envRows(spec.env)) }
  }
  return null
}

/** The services an SDL declares, with the image each runs, for the insert target picker. */
export function sdlServices(text: string): Array<{ name: string; image: string }> {
  const doc = parsed(text)
  const services = (doc?.toJS() as { services?: Record<string, { image?: unknown }> } | null | undefined)?.services
  if (!services || typeof services !== 'object') return []
  return Object.entries(services).map(([name, spec]) => ({ name, image: typeof spec?.image === 'string' ? spec.image : '' }))
}

/** The environment a service already sets. */
export function sdlServiceEnv(text: string, service: string): GuidedEnvironment[] {
  const doc = parsed(text)
  const services = (doc?.toJS() as { services?: Record<string, { env?: unknown }> } | null | undefined)?.services
  return envRows(services?.[service]?.env)
}

/**
 * Add `env` to one service's `env` list, in place in the document so comments
 * and the rest of the file survive. An entry whose key the service already
 * sets is replaced where it stands; `replaced` names those. Throws when the
 * text does not parse or the service is not a mapping: the editor shows that
 * instead of rewriting a document it cannot read.
 */
export function insertS3EnvIntoSdl(text: string, service: string, env: GuidedEnvironment[]): { text: string; replaced: string[] } {
  const doc = parsed(text)
  if (!doc) throw new Error('Fix the YAML errors first; the connection is inserted into a valid document only.')
  const node = doc.getIn(['services', service], true)
  if (!isMap(node)) throw new Error(`There is no service named ${service}.`)
  for (const entry of env) if (!KEY_PATTERN.test(entry.key)) throw new Error(`${entry.key} is not a valid variable name.`)
  let list = node.get('env', true)
  if (list === undefined || list === null || (isScalar(list) && list.value === null)) {
    node.set('env', doc.createNode([]))
    list = node.get('env', true)
  }
  if (!isSeq(list)) throw new Error(`services.${service}.env must be a list of "KEY=value" strings.`)
  const seq = list as YAMLSeq
  const replaced: string[] = []
  for (const entry of env) {
    const line = `${entry.key}=${entry.value}`
    const index = seq.items.findIndex((item) => isScalar(item) && typeof item.value === 'string' && item.value.slice(0, item.value.indexOf('=')).trim() === entry.key)
    const scalar = doc.createNode(line)
    // Quoted, so a value with `: ` or ` #` stays one string (as guidedDraftToSdl writes them).
    scalar.type = 'QUOTE_DOUBLE'
    if (index >= 0) {
      seq.items[index] = scalar
      replaced.push(entry.key)
    } else {
      seq.items.push(scalar)
    }
  }
  seq.flow = false
  return { text: doc.toString(), replaced }
}
